import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Crown } from 'lucide-react';
type Row = {
  problem: string;
  // Each solution has two halves: what Razonamiento Avanzado gives,
  // then what Mega Pack Experto ADDS on top.
  avanzado: string;
  experto: string;
};
const ROWS: Row[] = [{
  problem: 'Querés dejar de estudiar de forma desordenada',
  avanzado: 'ordenás mejor tus decisiones clínicas',
  experto: 'además sumás una guía práctica para aplicar lo aprendido'
}, {
  problem: 'Sabés teoría, pero te cuesta resolver casos reales',
  avanzado: 'entrenás el análisis paso a paso de casos clínicos',
  experto: 'además incorporás herramientas para pasar del caso a la intervención'
}, {
  problem: 'Dudás al momento de decidir qué hacer con el paciente',
  avanzado: 'fortalecés tu criterio clínico',
  experto: 'además sumás ejercicios, progresiones y programas funcionales'
}, {
  problem: 'Querés sentirte más seguro en internado, prácticas o consultorio',
  avanzado: 'ganás más claridad para razonar y defender decisiones',
  experto: 'además contás con recursos concretos para aplicar en consultorio'
}, {
  problem: 'Buscás una formación más completa y más aplicable',
  avanzado: 'llevás tu análisis clínico a otro nivel',
  experto: 'integrás teoría, razonamiento clínico y ejercicio terapéutico en una sola experiencia'
}];
export function ExpertoValueAdds() {
  return <section className="relative overflow-hidden bg-navy-950 border-y border-white/5 py-14 sm:py-20 md:py-24" data-id="element-94">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" data-id="element-95">
        <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(rgba(201,168,76,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.4) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} data-id="element-96" />
      </div>

      {/* Ambient glows — gold + emerald to hint at both tiers */}
      <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] bg-gold/[0.06] blur-[120px] rounded-full pointer-events-none" data-id="element-97"></div>
      <div className="absolute bottom-1/4 -left-32 w-[500px] h-[500px] bg-emerald-500/[0.04] blur-[120px] rounded-full pointer-events-none" data-id="element-98"></div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-99">
        {/* Header */}
        <div className="mb-10 sm:mb-14 max-w-3xl" data-id="element-100">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gold/30 bg-gold/5 text-gold text-[9px] sm:text-xs font-bold tracking-[0.15em] uppercase mb-4 sm:mb-5" data-id="element-101">
            <Crown size={11} className="text-gold" data-id="element-102" />
            <span data-id="element-103">Comparativa de packs</span>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.05
        }} className="text-3xl sm:text-4xl md:text-5xl font-heading font-extrabold uppercase tracking-tight text-white leading-[1.05]" data-id="element-104">
            ¿Qué{' '}
            <span className="font-editorial italic text-gold font-medium normal-case" data-id="element-105">
              adquirís
            </span>{' '}
            con cada pack?
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="mt-4 sm:mt-5 text-base sm:text-lg text-gray-400 leading-relaxed" data-id="element-106">
            Compará lo que suma el{' '}
            <span className="text-emerald-300 font-semibold" data-id="element-107">
              Pack + Razonamiento Avanzado
            </span>{' '}
            y lo que desbloqueás con el{' '}
            <span className="text-gold font-semibold" data-id="element-108">Mega Pack Experto</span>.
          </motion.p>
        </div>

        {/* Rows */}
        <div className="border-t border-white/10" data-id="element-109">
          {ROWS.map((row, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          delay: i * 0.05
        }} className="group grid md:grid-cols-[1fr_auto_1.4fr] gap-4 md:gap-8 items-start py-7 sm:py-9 border-b border-white/10 transition-colors hover:bg-white/[0.015]" data-id="element-110">
              {/* Left — problem */}
              <div className="flex items-start gap-3 md:gap-4" data-id="element-111">
                <span className="hidden md:flex flex-shrink-0 w-8 h-8 rounded-full bg-white/5 border border-white/10 text-gray-500 text-xs font-bold items-center justify-center mt-1" data-id="element-112">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="text-lg sm:text-xl md:text-[1.35rem] font-medium text-white/90 leading-snug" data-id="element-113">
                  {row.problem}
                </p>
              </div>

              {/* Arrow (desktop only) */}
              <div className="hidden md:flex items-center justify-center text-gold/40 group-hover:text-gold transition-colors mt-2" data-id="element-114">
                <ArrowRight size={20} strokeWidth={2} data-id="element-115" />
              </div>

              {/* Right — progressive comparison */}
              <div className="md:pl-0 space-y-3 sm:space-y-3.5" data-id="element-116">
                {/* Tier 1 — Razonamiento Avanzado */}
                <div className="flex items-start gap-3 pl-4 md:pl-0 border-l-2 md:border-l-0 border-emerald-500/40" data-id="element-117">
                  <span className="hidden md:inline-flex flex-shrink-0 items-center text-[9px] font-bold tracking-[0.18em] uppercase text-emerald-300/90 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2 py-0.5 mt-1" data-id="element-118">
                    Avanzado
                  </span>
                  <p className="text-base sm:text-lg md:text-[1.15rem] text-white/85 leading-snug" data-id="element-119">
                    <span className="md:hidden font-bold uppercase tracking-wide text-[10px] text-emerald-300 mr-1" data-id="element-120">
                      Avanzado ·
                    </span>
                    Con{' '}
                    <span className="text-emerald-300 font-semibold" data-id="element-121">
                      Razonamiento Avanzado
                    </span>{' '}
                    {row.avanzado}.
                  </p>
                </div>

                {/* Tier 2 — Mega Pack Experto */}
                <div className="flex items-start gap-3 pl-4 md:pl-0 border-l-2 md:border-l-0 border-gold/50" data-id="element-122">
                  <span className="hidden md:inline-flex flex-shrink-0 items-center text-[9px] font-bold tracking-[0.18em] uppercase text-gold bg-gold/10 border border-gold/40 rounded-full px-2 py-0.5 mt-1" data-id="element-123">
                    Experto
                  </span>
                  <p className="text-base sm:text-lg md:text-[1.15rem] text-white leading-snug" data-id="element-124">
                    <span className="md:hidden font-bold uppercase tracking-wide text-[10px] text-gold mr-1" data-id="element-125">
                      Experto ·
                    </span>
                    Con{' '}
                    <span className="text-gold font-semibold" data-id="element-126">
                      Mega Pack Experto
                    </span>{' '}
                    {row.experto}.
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* Closing block */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="mt-10 sm:mt-14 max-w-4xl" data-id="element-127">
          <p className="text-xl sm:text-2xl md:text-[1.75rem] font-heading font-bold text-white leading-tight" data-id="element-128">
            <span className="text-emerald-300" data-id="element-129">Razonamiento Avanzado</span> te
            ayuda a{' '}
            <span className="font-editorial italic font-medium" data-id="element-130">
              pensar mejor
            </span>{' '}
            los casos.
          </p>
          <p className="mt-3 sm:mt-4 text-xl sm:text-2xl md:text-[1.75rem] font-heading font-bold text-white leading-tight" data-id="element-131">
            <span className="text-gold" data-id="element-132">Mega Pack Experto</span> te ayuda a
            pensar mejor y además a{' '}
            <span className="font-editorial italic font-medium text-gold/90" data-id="element-133">
              intervenir con más seguridad
            </span>
            .
          </p>
        </motion.div>
      </div>
    </section>;
}